<template>
  <div>
    <BaseDialog contentClass="invitation" ref="dialog" sidePanel @closeModal="closeDialog" @dialog-closed="onClosed">
      <template slot="title">{{ $t('page.admin-users.access-dialog.title') }}</template>
      <template slot="content">
        <div v-if="user" class="new-invitation__form">
          <div class="login-form__section-small">
            <FormLabel>{{ $t('login.id') }}</FormLabel>
            <p class="edit-user-access__email">{{ user.email }}</p>
          </div>

          <div class="login-form__section-small edit-user-access__section">
            <template v-if="canChangeRole">
              <BaseSelect
                name="role"
                :label="$t('page.admin-invites.role')"
                :value="selectedRole"
                :items="roleOptions"
                item-text="text"
                item-value="value"
                :is-disabled="isSavingRole"
                @change="onRoleChange"
              />
              <p class="edit-user-access__note">{{ $t('page.admin-users.access-dialog.role-hint') }}</p>
            </template>
            <template v-else>
              <FormLabel>{{ $t('page.admin-invites.role') }}</FormLabel>
              <div class="edit-user-access__role">
                <BaseTag tone="neutral" preset="soft">{{ $t(`roles.${user.role}`) }}</BaseTag>
              </div>
              <p class="edit-user-access__note">{{ roleLockedReason }}</p>
            </template>
            <p v-if="roleError" class="new-invitation__role-error">{{ roleError }}</p>
          </div>

          <div class="login-form__section-small edit-user-access__section">
            <FormLabel>{{ $t('page.admin-users.permissions') }}</FormLabel>
            <p v-if="isLoading" class="edit-user-access__note">
              {{ $t('page.admin-users.access-dialog.loading') }}
            </p>
            <p v-else-if="loadFailed" class="new-invitation__role-error">
              {{ $t('page.admin-users.access-dialog.error-loading') }}
            </p>
            <p v-else-if="!editable.length" class="edit-user-access__note">
              {{ $t('page.admin-users.access-dialog.empty') }}
            </p>
            <ul v-else class="edit-user-access__list">
              <li v-for="permission in editable" :key="permission.id" class="edit-user-access__item">
                <div>
                  <p class="edit-user-access__label">{{ $t(`${i18nKey(permission.id)}.label`) }}</p>
                  <p class="edit-user-access__description">{{ $t(`${i18nKey(permission.id)}.description`) }}</p>
                </div>
                <BaseToggle
                  :toggle-key="permission.id"
                  :aria-label="$t(`${i18nKey(permission.id)}.label`)"
                  :value="isOn(permission.id)"
                  :disabled="permission.id in pending"
                  @change="onToggle(permission.id, $event)"
                />
              </li>
            </ul>
            <p v-if="editable.length" class="edit-user-access__note">
              {{ $t('page.admin-users.access-dialog.permissions-hint') }}
            </p>
            <p v-if="permissionsError" class="new-invitation__role-error">{{ permissionsError }}</p>
          </div>
        </div>
      </template>
    </BaseDialog>

    <ConfirmationDialog
      ref="privilegedConfirm"
      :buttonText="$t('page.admin-users.access-dialog.confirm-role')"
      is-danger
      @execute="onPrivilegedConfirmed"
      @closed="onPrivilegedClosed"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n v-if="user && roleToConfirm" :path="privilegedConfirmPath">
            <template v-slot:email>
              <span class="confirmation-dialog__user">{{ user.email }}</span>
            </template>
            <template v-slot:role>
              <span class="confirmation-dialog__action">{{ $t('roles.' + roleToConfirm) }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BaseSelect from '@/components/base/BaseSelect';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseToggle from '@/components/base/BaseToggle';
import FormLabel from '@/components/form/FormLabel.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import { grantablePermissions, permissionI18nKey } from '@/models/permission';
import { canEditRole, grantableRoles, isAdmin, isPrivilegedRole, isProtectedRole } from '@/models/role';
import { PermissionsService } from '@/services/permissions.service';

import { mapActions, mapGetters } from 'vuex';

/**
 * A user's role and permissions in one side panel. Both save as soon as they change; the list
 * behind refreshes once, on close, if anything did.
 */
export default {
  name: 'EditUserAccessDialog',
  components: {
    BaseDialog,
    BaseSelect,
    BaseTag,
    BaseToggle,
    ConfirmationDialog,
    FormLabel,
  },
  data() {
    return {
      user: null,
      // Bumped on every open and close, so a response landing later knows the panel moved on.
      session: 0,
      hasChanges: false,

      // Bound to the select, which only reverts when this changes back.
      selectedRole: null,
      roleToConfirm: null,
      isAwaitingConfirm: false,
      isSavingRole: false,
      roleError: '',

      catalogue: [],
      isLoading: false,
      loadFailed: false,
      // Permission id → the state it is being switched to, while the request is in flight.
      pending: {},
      permissionsError: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenantModules', ['tenantModules']),
    canChangeRole() {
      return this.user.id !== this.currentUser.id && canEditRole(this.currentUser.role, this.user.role);
    },
    roleLockedReason() {
      if (this.user.id === this.currentUser.id) {
        return this.$t('page.admin-users.access-dialog.role-locked-self');
      }
      if (isProtectedRole(this.user.role)) {
        return this.$t('page.admin-users.access-dialog.role-locked-protected');
      }
      return this.$t('page.admin-users.access-dialog.role-locked-higher');
    },
    /** Platform roles reach every client account, so their warning says so rather than "this account". */
    privilegedConfirmPath() {
      return isAdmin(this.roleToConfirm)
        ? 'page.admin-users.privileged-confirm-platform'
        : 'page.admin-users.privileged-confirm';
    },
    roleOptions() {
      return grantableRoles(this.currentUser.role).map((role) => ({ text: this.$t(`roles.${role}`), value: role }));
    },
    editable() {
      return grantablePermissions(this.catalogue, this.tenantModules);
    },
  },
  methods: {
    ...mapActions('users', ['setUserPermission', 'updateUserRole']),
    i18nKey: permissionI18nKey,
    async openDialog(user) {
      this.session += 1;
      this.user = user;
      this.hasChanges = false;
      this.selectedRole = user.role;
      this.roleToConfirm = null;
      this.isAwaitingConfirm = false;
      this.isSavingRole = false;
      this.roleError = '';
      this.pending = {};
      this.permissionsError = '';
      this.$refs.dialog.openModal();
      await this.loadCatalogue();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    // Every close lands here, Esc and outside-click included, which skip `closeModal`.
    onClosed() {
      this.session += 1;
      if (this.hasChanges) {
        this.$emit('actionSucceeded');
      }
      this.hasChanges = false;
    },
    /** False when the panel closed or moved on mid-request; the list still needs refreshing then. */
    isCurrent(session) {
      if (session === this.session) {
        return true;
      }
      this.$emit('actionSucceeded');
      return false;
    },

    // ----- Role -----
    onRoleChange(role) {
      this.selectedRole = role;
      this.roleError = '';
      if (role === this.user.role) return;

      if (isPrivilegedRole(role)) {
        this.roleToConfirm = role;
        this.isAwaitingConfirm = true;
        this.$refs.privilegedConfirm.openDialog();
        return;
      }

      this.saveRole(role);
    },
    onPrivilegedConfirmed() {
      this.isAwaitingConfirm = false;
      this.saveRole(this.roleToConfirm);
    },
    // Cancel and the close button both land here; only an unconfirmed choice is undone.
    onPrivilegedClosed() {
      if (this.isAwaitingConfirm) {
        this.isAwaitingConfirm = false;
        this.selectedRole = this.user.role;
      }
    },
    async saveRole(role) {
      const session = this.session;
      this.isSavingRole = true;
      try {
        const updated = await this.updateUserRole({ id: this.user.id, role });
        if (!this.isCurrent(session)) return;
        this.user = { ...this.user, role: updated.role };
        this.selectedRole = updated.role;
        this.hasChanges = true;
        this.$toast.success(this.$t('page.admin-users.role-updated'));
      } catch (error) {
        if (session !== this.session) return;
        this.selectedRole = this.user.role;
        this.roleError =
          error && error.status === 403
            ? this.$t('page.admin-users.role-forbidden')
            : this.$t('page.admin-users.error-updating-role');
      } finally {
        if (session === this.session) {
          this.isSavingRole = false;
        }
      }
    },

    // ----- Permissions -----
    async loadCatalogue() {
      const session = this.session;
      this.isLoading = true;
      this.loadFailed = false;
      try {
        const catalogue = await PermissionsService.getPermissions();
        if (session !== this.session) return;
        this.catalogue = catalogue;
      } catch (error) {
        if (session !== this.session) return;
        this.catalogue = [];
        this.loadFailed = true;
      } finally {
        if (session === this.session) {
          this.isLoading = false;
        }
      }
    },
    isOn(id) {
      return id in this.pending ? this.pending[id] : (this.user.permissions ?? []).includes(id);
    },
    async onToggle(id, granted) {
      const session = this.session;
      this.permissionsError = '';
      this.pending = { ...this.pending, [id]: granted };
      try {
        const updated = await this.setUserPermission({ id: this.user.id, permissionId: id, granted });
        if (!this.isCurrent(session)) return;
        this.user = { ...this.user, permissions: updated.permissions ?? [] };
        this.hasChanges = true;
        this.$toast.success(
          this.$t(granted ? 'page.admin-users.permission-granted' : 'page.admin-users.permission-revoked'),
        );
      } catch (error) {
        if (session !== this.session) return;
        if (error && error.status === 403) {
          this.permissionsError = this.$t('page.admin-users.permissions-forbidden');
        } else if (error && error.status === 400) {
          this.permissionsError = this.$t('page.admin-users.permissions-unavailable');
        } else {
          this.permissionsError = this.$t('page.admin-users.error-updating-permissions');
        }
      } finally {
        const pending = { ...this.pending };
        delete pending[id];
        this.pending = pending;
      }
    },
  },
};
</script>

<style lang="scss">
.edit-user-access {
  &__email {
    margin: 4px 0 0;
    font-size: 16px;
    color: $color-normal;
  }

  &__section {
    margin-top: 24px;
  }

  &__role {
    margin-top: 4px;
  }

  &__list {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid $color-slate-200;
  }

  &__label {
    margin: 0;
    font-size: 15px;
    color: $color-normal;
  }

  &__description {
    margin: 2px 0 0;
    font-size: 13px;
    color: $color-slate-600;
  }

  &__note {
    margin: 8px 0 0;
    font-size: 13px;
    color: $color-slate-600;
  }
}
</style>
